import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  MessageSquareCode,
  RefreshCw,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import {
  CodeSourceError,
  getCodeSource,
  type CodeSourceResponse,
} from '@/services/workflow/get-code-source';
import { useNodeExplorerStore } from '@/stores/node-explorer-store';
import { cn } from '@/lib/utils';

interface SourceCodePanelProps {
  orgId: string;
  workflowId: string;
  runId: string;
  nodeId: string;
}

export function isSourceViewAvailable({
  isSingleNode,
  nodeType,
  orgId,
  workflowId,
}: {
  isSingleNode: boolean;
  nodeType?: string;
  orgId?: string | null;
  workflowId?: string | null;
  /** Kept for call-site compatibility; a run is no longer required --
   * without one the tab opens the editable current-pin view. */
  runId?: string | null;
}): boolean {
  return (
    isSingleNode &&
    nodeType === 'custom_code_node' &&
    Boolean(orgId && workflowId && workflowId !== 'new')
  );
}

const shortSha = (sha: string) => sha.slice(0, 12);

export function SourceCodePanel({
  orgId,
  workflowId,
  runId,
  nodeId,
}: SourceCodePanelProps) {
  const [source, setSource] = useState<CodeSourceResponse | null>(null);
  const [selectedPath, setSelectedPath] = useState('');
  const [error, setError] = useState<CodeSourceError | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestVersion, setRequestVersion] = useState(0);
  const [copied, setCopied] = useState(false);
  const openSourceChat = useNodeExplorerStore((state) => state.openSourceChat);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getCodeSource(orgId, workflowId, runId, nodeId, controller.signal)
      .then((result) => {
        setSource(result);
        setSelectedPath((current) => {
          if (result.files.some((file) => file.relative_path === current)) {
            return current;
          }
          return (
            result.provenance.entrypoint || result.files[0]?.relative_path || ''
          );
        });
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            caught instanceof CodeSourceError
              ? caught
              : new CodeSourceError('Source could not be loaded.'),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [orgId, workflowId, runId, nodeId, requestVersion]);

  const selectedFile = useMemo(
    () =>
      source?.files.find((file) => file.relative_path === selectedPath) ??
      source?.files[0] ??
      null,
    [source, selectedPath],
  );

  if (loading) {
    return (
      <div
        data-testid='source-loading'
        className='flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground'
      >
        <Loader2 className='size-4 animate-spin' />
        Loading pinned source…
      </div>
    );
  }

  if (error || !source) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center'>
        <AlertTriangle className='size-6 text-amber-500' />
        <div>
          <p className='text-sm font-medium'>Source unavailable</p>
          <p className='mt-1 max-w-md text-xs text-muted-foreground'>
            {error?.message ?? 'Source could not be loaded.'}
          </p>
          {error?.code && (
            <p className='mt-1 font-mono text-[11px] text-muted-foreground'>
              {error.code}
            </p>
          )}
        </div>
        <Button
          size='sm'
          variant='outline'
          onClick={() => setRequestVersion((value) => value + 1)}
        >
          <RefreshCw className='size-3.5' />
          Retry
        </Button>
      </div>
    );
  }

  const { provenance } = source;
  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
      <div className='shrink-0 space-y-3 border-b p-4'>
        <div className='flex flex-wrap items-start justify-between gap-2'>
          <div>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold'>
                Immutable run source
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-medium',
                  provenance.matches_execution_metadata
                    ? 'bg-emerald-500/10 text-emerald-600'
                    : 'bg-amber-500/10 text-amber-600',
                )}
              >
                {provenance.matches_execution_metadata
                  ? 'Execution matched'
                  : 'Metadata differs'}
              </span>
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>
              Historical code is read-only. Changes create a new commit and
              require a separate workflow repin.
            </p>
          </div>
          <Button
            data-testid='ask-orchestrator-to-modify'
            size='sm'
            onClick={() =>
              openSourceChat({
                workflow_id: provenance.workflow_id,
                workflow_version_id: provenance.workflow_version_id,
                run_id: provenance.run_id,
                node_id: provenance.node_id,
                selected_file:
                  selectedFile?.relative_path ?? provenance.entrypoint,
              })
            }
          >
            <MessageSquareCode className='size-3.5' />
            Ask orchestrator to modify
          </Button>
        </div>

        <dl className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs'>
          <dt className='text-muted-foreground'>Repository</dt>
          <dd className='truncate font-mono' title={provenance.repo_url}>
            {provenance.repo}
          </dd>
          <dt className='text-muted-foreground'>Node path</dt>
          <dd className='truncate font-mono'>{provenance.script_path}</dd>
          <dt className='text-muted-foreground'>Entrypoint</dt>
          <dd className='truncate font-mono'>{provenance.entrypoint}</dd>
          <dt className='text-muted-foreground'>Pinned SHA</dt>
          <dd className='font-mono' title={provenance.pinned_sha}>
            {shortSha(provenance.pinned_sha)}
          </dd>
          <dt className='text-muted-foreground'>Executed SHA</dt>
          <dd className='font-mono' title={provenance.resolved_execution_sha}>
            {shortSha(provenance.resolved_execution_sha)}
          </dd>
          <dt className='text-muted-foreground'>Manifest</dt>
          <dd className='truncate font-mono' title={provenance.manifest_path}>
            {provenance.manifest_name} ({provenance.manifest_path})
          </dd>
          <dt className='text-muted-foreground'>Workflow version</dt>
          <dd className='font-mono'>
            {provenance.workflow_version_number} ·{' '}
            {provenance.workflow_version_id.slice(0, 8)}
          </dd>
        </dl>
        {provenance.repository_provenance_note && (
          <div className='flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-700 dark:text-amber-300'>
            <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
            {provenance.repository_provenance_note}
          </div>
        )}
      </div>

      <div className='flex min-h-0 flex-1'>
        <div className='w-44 shrink-0 overflow-y-auto border-r p-2'>
          {source.files.map((file) => (
            <button
              key={file.path}
              type='button'
              onClick={() => setSelectedPath(file.relative_path)}
              className={cn(
                'mb-1 w-full rounded px-2 py-1.5 text-left font-mono text-xs',
                file.relative_path === selectedFile?.relative_path
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60',
              )}
              title={file.relative_path}
            >
              <span className='block truncate'>{file.relative_path}</span>
              <span className='text-[10px]'>{file.size_bytes} B</span>
            </button>
          ))}
        </div>
        <div className='flex min-w-0 flex-1 flex-col'>
          <div className='flex h-10 shrink-0 items-center justify-between gap-2 border-b px-3'>
            <span className='truncate font-mono text-xs'>
              {selectedFile?.relative_path}
            </span>
            <div className='flex items-center gap-1'>
              {selectedFile?.permalink && (
                <Button size='icon' variant='ghost' asChild>
                  <a
                    href={selectedFile.permalink}
                    target='_blank'
                    rel='noreferrer'
                    aria-label='Open immutable GitHub permalink'
                  >
                    <ExternalLink className='size-3.5' />
                  </a>
                </Button>
              )}
              <Button
                size='icon'
                variant='ghost'
                aria-label='Copy source'
                onClick={async () => {
                  if (!selectedFile) return;
                  await navigator.clipboard.writeText(selectedFile.content);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1200);
                }}
              >
                {copied ? (
                  <Check className='size-3.5' />
                ) : (
                  <Copy className='size-3.5' />
                )}
              </Button>
            </div>
          </div>
          <div className='min-h-0 flex-1 overflow-auto bg-muted/20 p-4'>
            {selectedFile && (
              <SyntaxHighlighter
                code={selectedFile.content}
                language={selectedFile.language}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
